import React from 'react';
import { useTranslation } from 'react-i18next';

// styles
import * as Styled from 'appExplainers/styles/Faq.style';
import * as Text from 'theme/components/Text.style';

type BulletRef = { labelKey?: string; textKey: string };

type BlockRef =
  | { kind: 'paragraph'; textKey: string }
  | { kind: 'bullets'; items: BulletRef[] };

interface EntryRef {
  headingKey?: string;
  blocks: BlockRef[];
}

interface SectionRef {
  titleKey: string;
  entries: EntryRef[];
}

const SECTIONS: SectionRef[] = [
  {
    titleKey: 'pageFaq.introduction.title',
    entries: [
      {
        headingKey: 'pageFaq.introduction.aboutNurau.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.introduction.aboutNurau.body',
          },
        ],
      },
    ],
  },
  {
    titleKey: 'pageFaq.gettingStarted.title',
    entries: [
      {
        headingKey: 'pageFaq.gettingStarted.accessing.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.gettingStarted.accessing.body',
          },
        ],
      },
    ],
  },
  {
    titleKey: 'pageFaq.usingNurau.title',
    entries: [
      {
        headingKey: 'pageFaq.usingNurau.whatItDoes.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.usingNurau.whatItDoes.body',
          },
        ],
      },
    ],
  },
  {
    titleKey: 'pageFaq.troubleshooting.title',
    entries: [
      {
        headingKey: 'pageFaq.troubleshooting.accessLink.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.troubleshooting.accessLink.body',
          },
        ],
      },
      {
        headingKey: 'pageFaq.troubleshooting.invited.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.troubleshooting.invited.body',
          },
        ],
      },
      {
        headingKey: 'pageFaq.troubleshooting.noWorkEmail.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.troubleshooting.noWorkEmail.body',
          },
        ],
      },
      {
        headingKey: 'pageFaq.troubleshooting.notWorking.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.troubleshooting.notWorking.body',
          },
        ],
      },
    ],
  },
  {
    titleKey: 'pageFaq.accountPrivacy.title',
    entries: [
      {
        headingKey: 'pageFaq.accountPrivacy.data.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.accountPrivacy.data.body',
          },
        ],
      },
    ],
  },
  {
    titleKey: 'pageFaq.future.title',
    entries: [
      {
        headingKey: 'pageFaq.future.updates.heading',
        blocks: [{ kind: 'paragraph', textKey: 'pageFaq.future.updates.body' }],
      },
      {
        headingKey: 'pageFaq.future.features.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.future.features.body' },
        ],
      },
    ],
  },
  {
    titleKey: 'pageFaq.general.title',
    entries: [
      {
        headingKey: 'pageFaq.general.chatgpt.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.chatgpt.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.coaching.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.coaching.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.relevance.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.relevance.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.intelligence.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.general.intelligence.body',
          },
        ],
      },
      {
        headingKey: 'pageFaq.general.compliance.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.compliance.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.downloadable.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.downloadable.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.device.heading',
        blocks: [{ kind: 'paragraph', textKey: 'pageFaq.general.device.body' }],
      },
      {
        headingKey: 'pageFaq.general.offline.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.offline.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.homeScreen.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.homeScreen.intro' },
          {
            kind: 'bullets',
            items: [
              {
                labelKey: 'pageFaq.general.homeScreen.iphoneLabel',
                textKey: 'pageFaq.general.homeScreen.iphoneText',
              },
              {
                labelKey: 'pageFaq.general.homeScreen.androidLabel',
                textKey: 'pageFaq.general.homeScreen.androidText',
              },
            ],
          },
        ],
      },
      {
        headingKey: 'pageFaq.general.languages.heading',
        blocks: [
          { kind: 'paragraph', textKey: 'pageFaq.general.languages.body' },
        ],
      },
      {
        headingKey: 'pageFaq.general.bookmarks.heading',
        blocks: [
          {
            kind: 'bullets',
            items: [
              {
                labelKey: 'pageFaq.general.bookmarks.chromeLabel',
                textKey: 'pageFaq.general.bookmarks.chromeText',
              },
              {
                labelKey: 'pageFaq.general.bookmarks.safariLabel',
                textKey: 'pageFaq.general.bookmarks.safariText',
              },
              {
                labelKey: 'pageFaq.general.bookmarks.edgeLabel',
                textKey: 'pageFaq.general.bookmarks.edgeText',
              },
            ],
          },
        ],
      },
      {
        headingKey: 'pageFaq.general.savingContent.heading',
        blocks: [
          {
            kind: 'paragraph',
            textKey: 'pageFaq.general.savingContent.body',
          },
        ],
      },
      {
        headingKey: 'pageFaq.general.social.heading',
        blocks: [{ kind: 'paragraph', textKey: 'pageFaq.general.social.body' }],
      },
      {
        headingKey: 'pageFaq.general.delete.heading',
        blocks: [{ kind: 'paragraph', textKey: 'pageFaq.general.delete.body' }],
      },
    ],
  },
];

function Faq(): JSX.Element {
  const { t } = useTranslation('pageAbout');

  const renderBlock = (block: BlockRef, index: number): JSX.Element => {
    if (block.kind === 'paragraph') {
      return <Text.P2 key={index}>{t(block.textKey)}</Text.P2>;
    }
    return (
      <Styled.BulletList key={index}>
        {block.items.map((item, i) => (
          <Styled.BulletItem key={i}>
            <Text.P2>
              {item.labelKey && (
                <Text.P2Semibold as="span">
                  {`${t(item.labelKey)}: `}
                </Text.P2Semibold>
              )}
              {t(item.textKey)}
            </Text.P2>
          </Styled.BulletItem>
        ))}
      </Styled.BulletList>
    );
  };

  const renderEntry = (entry: EntryRef, index: number): JSX.Element => (
    <Styled.Entry key={index}>
      {entry.headingKey && (
        <Text.P3Semibold>{t(entry.headingKey)}</Text.P3Semibold>
      )}
      {entry.blocks.map(renderBlock)}
    </Styled.Entry>
  );

  return (
    <Styled.Wrapper>
      {SECTIONS.map((section, index) => (
        <Styled.Section key={index}>
          <Text.H5>{t(section.titleKey)}</Text.H5>
          {section.entries.map(renderEntry)}
        </Styled.Section>
      ))}
    </Styled.Wrapper>
  );
}

export default Faq;
